import { Question } from 'src/app/models/question.model';
import { QuestionnaireService } from './../questionnaire/questionnaire.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { waitForCondition } from 'src/app/utilities/wait-for';
import { TranslateService } from '@ngx-translate/core';
import { Platform, LoadingController } from '@ionic/angular';
import { ConnectService } from 'src/app/services/connect/connect.service';
import { AuthService } from '../auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { resolveFileSystemURL, fileEntryToArrayBuffer } from '../../utilities/file';
import { FileEntry } from '@ionic-native/file/ngx';
import { ObjectUrlService } from '../object-url/object-url.service';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DirectUpload, DirectUploadFile } from './activestorage/direct_upload';
import { SentryService, SentrySeverityLevel } from '../sentry/sentry.service';
import { Router } from '@angular/router';
import { TranslateConfigService } from '../translate/translate-config.service';

@Injectable()
export class UploadService {
  public uploadingFiles: Map<string, UploadInfo> = new Map<string, UploadInfo>(); // uploadId => progress (0-100)
  resolveFileSystemURL = resolveFileSystemURL;
  fileEntryToArrayBuffer = fileEntryToArrayBuffer;
  directUploadUrl = '/direct_uploads';
  redirectedBackToQuestionIdx = 99999;
  uploadErrorSubject = new Subject<Question>();
  redirectedBackSubject = new Subject<number>();

  constructor(
    public http: HttpClient,
    public authSvc: AuthService,
    private platform: Platform,
    private objectURLsvc: ObjectUrlService,
    private connectSvc: ConnectService,
    private sentrySvc: SentryService,
    private translateSvc: TranslateService,
    public translateConfigSvc: TranslateConfigService,
    private loadingController: LoadingController,
    private alertSvc: AlertService,
    private router: Router,
    private questionnaireSvc: QuestionnaireService
  ) {}

  async uploadFile(file: UploadData, object: string, question: Question): Promise<string> {
    question.uploading = true;
    const uploadId = uuidv4();
    let signedId = null;
    this.uploadingFiles.set(uploadId, { progress: 0, questionnaireId: this.getQuestionnaireIdFromPath(object), questionId: question.id });
    let token = this.authSvc.token;
    this.authSvc.readToken().then(storageToken => (token = storageToken));

    let fileBlob: DirectUploadFile = null;
    try {
      if (this.platform.is('cordova') && !question.manualVideoPhotoUpload) {
        const fileEntry = (await this.resolveFileSystemURL(file.fullPath)) as FileEntry;
        const fileBuffer = await this.fileEntryToArrayBuffer(fileEntry);
        fileBlob = new Blob([fileBuffer], { type: file.type }) as DirectUploadFile;
      } else {
        fileBlob = this.objectURLsvc.getFromObjectURL(file.fullPath) as DirectUploadFile;
      }

      this.redirectedBackToQuestionIdx = this.questionnaireSvc.currentIndex;
      if (!fileBlob.name) {
        fileBlob.name = file.name;
      }
      signedId = await this.directUpload(fileBlob, token, uploadId);
      if (!this.uploadingFiles.has(uploadId)) {
        signedId = null;
        throw UploadStatus.cancelled;
      }
    } catch (error) {
      if (error === UploadStatus.cancelled) {
        return signedId;
      }

      this.uploadErrorSubject.next(question);
      console.error(error);
      this.sentrySvc.setMessageForSentryWithErrorInfo('Error while uploading photo/video', SentrySeverityLevel.Critical, {
        error,
        question,
      });

      this.showErrorAndRedirectBackToQuestion(object, question);
    }

    this.clearUploadingFile(question);

    return signedId;
  }

  showErrorAndRedirectBackToQuestion(path: string, question: Question) {
    const questionnaireId = this.getQuestionnaireIdFromPath(path);
    const backToIndex = this.questionnaireSvc.getIndexByQuestionId(question.id);
    const bringBackToTheQuestion = this.router.url.includes(`/questionnaire/${questionnaireId}`);
    this.alertSvc
      .presentAlertConfirmation(
        this.translateSvc.instant('UploadService.somethingWrong'),
        '',
        this.translateSvc.instant('UploadService.failedToUpload'),
        this.translateSvc.instant('CommonTexts.okGotIt')
      )
      .then(() => {
        if (backToIndex !== this.redirectedBackToQuestionIdx && bringBackToTheQuestion) {
          this.questionnaireSvc.setQuestion(backToIndex);
          this.redirectedBackSubject.next(backToIndex);
          this.redirectedBackToQuestionIdx = backToIndex;
        }
      });
  }

  isUploading(): boolean {
    return this.uploadingFiles.size > 0;
  }

  getUploadingProgress(): number {
    let total = 0;
    this.uploadingFiles.forEach(uploadInfo => {
      total += uploadInfo.progress;
    });
    if (this.uploadingFiles.size === 0) {
      return 100;
    }
    return total / this.uploadingFiles.size;
  }

  getUploadingQuestionnaireIds(): number[] {
    const questionnaireIds: number[] = [];
    this.uploadingFiles.forEach(uploadInfo => {
      questionnaireIds.push(uploadInfo.questionnaireId);
    });
    return [...new Set(questionnaireIds)];
  }

  // istanbul ignore next
  createDirectUploadInstance(file: DirectUploadFile, url: string, directUploadWillStoreFileWithXHR, directUploadWillCreateBlobWithXHR) {
    return new DirectUpload(file, url, { directUploadWillStoreFileWithXHR, directUploadWillCreateBlobWithXHR });
  }

  /**
   * example path:
   *    /scheduled_engagements/669722/results/3314/2673
   *    /program_reports/181/results/3320/2715
   * please keep the string synced with QuestionnaireService::generatePath()
   */
  getQuestionnaireIdFromPath(path: string): number {
    let objectRegex = new RegExp(/scheduled_engagements\/(?<questionnaireId>\d{0,})/g);
    if (typeof path === 'string' && path.includes('program_reports')) {
      objectRegex = new RegExp(/program_reports\/(?<questionnaireId>\d{0,})/g);
    }
    const matches = objectRegex.exec(path);
    const questionnaireId = matches?.groups?.questionnaireId;
    return questionnaireId ? Number(questionnaireId) : 0;
  }

  public storeUploadProgress(event: ProgressEvent, uploadId: string) {
    if (event.total && event.loaded < event.total) {
      const percentDone = Math.round((100 * event.loaded) / event.total);
      if (!this.uploadingFiles.has(uploadId)) {
        return;
      }
      const uploadInfo = this.uploadingFiles.get(uploadId);
      uploadInfo.progress = percentDone;
      this.uploadingFiles.set(uploadId, uploadInfo);
    }
  }

  public async directUpload(file: DirectUploadFile, token: string, uploadId: string): Promise<string> {
    const directUploadWillStoreFileWithXHR = request => {
      request.upload.addEventListener('progress', event => this.storeUploadProgress(event, uploadId));
    };

    const directUploadWillCreateBlobWithXHR = xhr => {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    };

    const logDirectUploadError = err => {
      console.error(err);
      this.sentrySvc.setMessageForSentryWithErrorInfo(`Failing to direct upload using activestorage`, SentrySeverityLevel.Critical, {
        file,
        err,
      });
    };

    return new Promise((resolve, reject) => {
      const url = this.connectSvc.apiRoot + this.directUploadUrl;
      const upload = this.createDirectUploadInstance(file, url, directUploadWillStoreFileWithXHR, directUploadWillCreateBlobWithXHR);
      try {
        upload.create((err, resp) => {
          if (err) {
            logDirectUploadError(err);
            reject(err);
          } else {
            resolve(resp.signed_id);
          }
        });
      } catch (err) {
        logDirectUploadError(err);
        reject(err);
      }
    });
  }

  async waitForUploadCompleted(): Promise<boolean> {
    let uploadError = false;
    return new Promise(async resolve => {
      if (!this.isUploading()) {
        resolve(true);
        return;
      }
      this.uploadErrorSubject.subscribe(() => {
        uploadError = true;
        resolve(false);
      });
      const uploadingMessage = this.translateSvc.instant('FloatingAlertComponent.dontCloseWhileUploading', {
        object: this.platform.is('cordova')
          ? this.translateSvc.instant('FloatingAlertComponent.thisApp')
          : this.translateSvc.instant('FloatingAlertComponent.thisBrowser'),
      });
      const getUploadingMessage = (progress: number): string => {
        return `<h1 class="wg-h-xs">
            ${this.translateSvc.instant('FloatingAlertComponent.uploadingSurvey')} (${progress}%)
          </h1>
          <small>
            ${uploadingMessage} 
          </small>`;
      };
      const loading = await this.loadingController.create({
        message: getUploadingMessage(0),
      });

      await loading.present();
      await waitForCondition(
        () => {
          loading.message = getUploadingMessage(this.getUploadingProgress());
          return !this.isUploading() || uploadError;
        },
        true,
        500
      );
      await loading.dismiss();
      resolve(true);
    });
  }

  clearUploadingFile(question: Question) {
    question.uploading = false;
    this.uploadingFiles.forEach((uploadInfo, uploadId) => {
      if (uploadInfo.questionId === question.id) {
        this.uploadingFiles.delete(uploadId);
      }
    });
  }
}

export interface UploadData {
  name: string;
  fullPath: string;
  type: string;
}

interface UploadInfo {
  progress: number;
  questionId: number;
  questionnaireId: number;
}

enum UploadStatus {
  cancelled = 'cancelled',
}
